import { AlertEntry, WorkbookRuleset } from "../services/workbookAnalyticsRulesEngine";

export const workbookRulesetsByCode: Record<string, WorkbookRuleset> = {
    "ReviewToolingTest": {
        version: {
            major: 0,
            minor: 1,
            patch: 0
        },
        name: "Review Tooling Workbook Ruleset",
        description: "Ruleset for Review Tooling Workbook",
        workbookCode: "ReviewToolingTest",
        alertCodes: {
            "GROSS_RENT_YOY_EXCEEDS_20": (_context) => ({
                level: "error",
                title: "Gross Rent Year-on-Year Comparison",
                message: `Year-over-year comparison from previous returns shows a variance greater than 20% on rental income. Review source documents to confirm this amount is correct.`
            }),
            "RATES_INVOICE_MISMATCH": (_context) => ({
                level: 'error',
                title: 'Rates Invoice Value Mismatch',
                message: `Council rate totals appear to differ from source documents. Review source documents to confirm values have been computed correctly.`,
            }),
            "INSURANCE_EXPENSE_MISMATCH": (_context) => ({
                level: 'error',
                title: 'Insurance Expense Value Mismatch',
                message: `The amount entered appears to be lower than the total from bank statements. Review insurance transactions to ensure all are included, then update the amount.`,
            }),
            "K75_CAPITALISATION_WARNING": (_context) => ({
                level: 'warning',
                title: 'Capitalisation',
                message: `Verify whether the below items should be classified as a capital item requiring depreciation.`,
            }),
            "CAPWORKS_PRIOR_YEAR_ERROR": (_context) => ({
                level: 'info',
                title: 'Prior Year Capital Works Expense Missing',
                message: `Some values appear to be missing. Review the sheet and ensure all fields have been entered correctly.`,
            })
        },
        worksheetRules: {
            "M25_RENTAL_PROPERTY": [
                {
                    workbookCode: 'ReviewToolingTest',
                    ruleId: 'gross-rent-year-on-year',
                    // name: 'Gross Rent Year-on-Year Comparison',
                    // description: 'Validates that year-on-year gross rent change is within 20%',
                    worksheetCode: 'M25 Rental Property',
                    inputs: [
                        { range: 'M25_GrossRent_2', required: true },
                        { range: 'M56', required: true }
                    ],
                    outputCells: ['L56'],
                    applicable: (context) => {
                        const m25Value = context.inputs['M25_GrossRent_2'];
                        const m56Value = context.inputs['M56'];

                        // Check if either is empty
                        const m25Empty = m25Value === null || m25Value === undefined || 
                            m25Value === '' || (typeof m25Value === 'string' && m25Value.trim() === '');
                        const m56Empty = m56Value === null || m56Value === undefined || 
                            m56Value === '' || (typeof m56Value === 'string' && m56Value.trim() === '');
                        if (m25Empty || m56Empty) return false;

                        const m25Num = typeof m25Value === 'number' ? m25Value : parseFloat(m25Value as string);
                        const m56Num = typeof m56Value === 'number' ? m56Value : parseFloat(m56Value as string);
                        
                        if (isNaN(m25Num) || isNaN(m56Num)) {
                            return false;
                        }
                        
                        if (m25Num === 0) {
                            return false;
                        }

                        return true;
                    },
                    run: async (context) => {
                        const m25Value = context.inputs['M25_GrossRent_2'];
                        const m56Value = context.inputs['M56'];

                        const m25Num = typeof m25Value === 'number' ? m25Value : parseFloat(m25Value as string);
                        const m56Num = typeof m56Value === 'number' ? m56Value : parseFloat(m56Value as string);
                        
                        // Calculate percentage difference from M25_GrossRent_2
                        const percentageDiff = Math.abs((m56Num - m25Num) / m25Num) * 100;
                        
                        if (percentageDiff > 20) {
                            context.raiseAlert({
                                code: 'GROSS_RENT_YOY_EXCEEDS_20'
                            });
                        }
                    }
                },
                
                {
                    workbookCode: 'ReviewToolingTest',
                    worksheetCode: 'M25 Rental Property',
                    ruleId: 'rates-invoice-match',
                    // name: 'Rates Invoice Value Match',
                    // description: 'Validates that rates value matches source documents in iManage',
                    inputs: [
                        { range: 'M25_Rates_2', required: true }
                    ],
                    outputCells: ['L64'],
                    applicable: (context) => {
                        const value = context.inputs['M25_Rates_2'];
                        
                        // Check if empty
                        const isEmpty = value === null || value === undefined || 
                            value === '' || (typeof value === 'string' && value.trim() === '');
                        
                        // Don't fire rule if empty
                        if (isEmpty) return false;
                        
                        // Convert to number for comparison
                        const numericValue = typeof value === 'number' ? value : parseFloat(value as string);
                        
                        if (isNaN(numericValue)) {
                            return false;
                        }
                        
                        // Don't fire rule if value is zero
                        if (numericValue === 0) {
                            return false;
                        }

                        return true;
                    },
                    run: (context) => {
                        const value = context.inputs['M25_Rates_2'];
                        const expectedValue = 3492.79;
                        const iManageUrl = 'https://findex-web.imanagework-au.com/work/web/r/libraries/FINDEX/folders/FINDEX!56342609?selectedItem=FINDEX!102647426.1';
                        
                        // Convert to number for comparison
                        const numericValue = typeof value === 'number' ? value : parseFloat(value as string);
                        
                        // Check if value matches exactly (using small epsilon for floating point comparison)
                        const epsilon = 0.001;
                        if (Math.abs(numericValue - expectedValue) > epsilon) {
                            context.raiseAlert({
                                // timestamp: Date.now(),
                                // id: nanoid(),
                                // ruleId: 'rates-invoice-match',
                                code: 'RATES_INVOICE_MISMATCH',
                                
                                // hyperlink: iManageUrl,
                                // highlightRanges: ['M25_Rates_2'],
                                // focusRange: 'M25_Rates_2',
                                // outputCells: ['L64']
                            });
                        }
                    }
                },
                
                {
                    workbookCode: 'ReviewToolingTest',
                    worksheetCode: 'M25 Rental Property',
                    ruleId: 'insurance-expense-match',
                    // name: 'Insurance Expense Value Match',
                    // description: 'Validates that insurance expense matches expected value',
                    inputs: [
                        { range: 'M25_Insurance_2', required: true }
                    ],
                    outputCells: ['L67'],
                    applicable: (context) => {
                        const value = context.inputs['M25_Insurance_2'];
                        
                        // Check if empty
                        const isEmpty = value === null || value === undefined || 
                            value === '' || (typeof value === 'string' && value.trim() === '');
                        
                        // Don't fire rule if empty
                        if (isEmpty) return false;
                        
                        // Convert to number for comparison
                        const numericValue = typeof value === 'number' ? value : parseFloat(value as string);
                        
                        if (isNaN(numericValue)) {
                            return false;
                        }
                        
                        // Don't fire rule if value is zero
                        if (numericValue === 0) return false;

                        return true;
                    },
                    run: (context) => {
                        const value = context.inputs['M25_Insurance_2'];
                        const results: AlertEntry[] = [];
                        const expectedValue = 6701.79;
                        
                        // Convert to number for comparison
                        const numericValue = typeof value === 'number' ? value : parseFloat(value as string);
                        
                        // Check if value matches exactly (using small epsilon for floating point comparison)
                        const epsilon = 0.001;
                        if (Math.abs(numericValue - expectedValue) > epsilon) {
                            context.raiseAlert({
                                // timestamp: Date.now(),
                                // id: nanoid(),
                                // ruleId: 'insurance-expense-match',
                                code: 'INSURANCE_EXPENSE_MISMATCH',


                                // highlightRanges: ['M25_Insurance_2'],
                                // focusRange: 'M25_Insurance_2',
                                // outputCells: ['L67']
                            });
                        }
                    }
                },
                
                {
                    workbookCode: 'ReviewToolingTest',
                    worksheetCode: 'M25 Rental Property',
                    ruleId: 'k75-capitalisation-review',
                    // name: 'Capitalisation Review',
                    // description: 'Warns if repair items have a value that may need to be capitalised',
                    inputs: [
                        { range: 'K75', required: true }
                    ],
                    outputCells: ['L75'],
                    applicable: (context) => {
                        const value = context.inputs['K75'];
                        
                        // Check if empty
                        const isEmpty = value === null || value === undefined || 
                            value === '' || (typeof value === 'string' && value.trim() === '');
                        
                        if (isEmpty) return false;
                        
                        // Convert to number
                        const numericValue = typeof value === 'number' ? value : parseFloat(value as string);
                        
                        // Rule applies if value is non-numeric OR numeric but not zero
                        if (isNaN(numericValue)) {
                            return true;
                        } else if (numericValue !== 0) {
                            return true;
                        }
                        
                        return false;
                    },
                    run: (context) => {
                        const value = context.inputs['K75'];
                        const results: AlertEntry[] = [];
                        
                        // Convert to number
                        const numericValue = typeof value === 'number' ? value : parseFloat(value as string);
                        
                        if (!isNaN(numericValue) && numericValue !== 0) {
                            // Has a numeric value that's not zero
                            context.raiseAlert({
                                // timestamp: Date.now(),
                                // id: nanoid(),
                                // ruleId: 'k75-capitalisation-review',
                                code: 'K75_CAPITALISATION_WARNING',
                                
                                // highlightRanges: ['K75'],
                                // focusRange: 'K75',
                                // outputCells: ['L75']
                            });
                        }
                        
                        return results;
                    }
                },
                
                {
                    workbookCode: 'ReviewToolingTest',
                    worksheetCode: 'M25 Rental Property',
                    ruleId: 'capworks-prior-year-check',
                    // name: 'Capital Works Prior Year Check',
                    // description: 'Validates that capital works expense exists if it existed in prior year',
                    inputs: [
                        { range: 'M25_CapWorks_2', required: true },
                        { range: 'M81', required: true },
                        { range: 'M25_GrossRent_2', required: true }
                    ],
                    outputCells: ['L81'],
                    applicable: (context) => {
                        const capWorksValue = context.inputs['M25_CapWorks_2'];
                        const m81Value = context.inputs['M81'];
                        const grossRentValue = context.inputs['M25_GrossRent_2'];
                        
                        // Check if M25_GrossRent_2 has a non-zero value
                        const grossRentEmpty = grossRentValue === null || grossRentValue === undefined || 
                            grossRentValue === '' || (typeof grossRentValue === 'string' && grossRentValue.trim() === '');
                        
                        let grossRentIsZero = false;
                        if (!grossRentEmpty) {
                            const grossRentNum = typeof grossRentValue === 'number' ? grossRentValue : parseFloat(grossRentValue as string);
                            grossRentIsZero = isNaN(grossRentNum) || grossRentNum === 0;
                        }
                        
                        // Don't fire rule if M25_GrossRent_2 is empty or zero
                        if (grossRentEmpty || grossRentIsZero) return false;
                        
                        // Check if M25_CapWorks_2 is empty or zero
                        const capWorksEmpty = capWorksValue === null || capWorksValue === undefined || 
                            capWorksValue === '' || (typeof capWorksValue === 'string' && capWorksValue.trim() === '');
                        
                        let capWorksIsZero = false;
                        if (!capWorksEmpty) {
                            const capWorksNum = typeof capWorksValue === 'number' ? capWorksValue : parseFloat(capWorksValue as string);
                            capWorksIsZero = !isNaN(capWorksNum) && capWorksNum === 0;
                        }
                        
                        // Check if M81 has a value
                        const m81Empty = m81Value === null || m81Value === undefined || 
                            m81Value === '' || (typeof m81Value === 'string' && m81Value.trim() === '');
                        
                        // If both empty, no issue
                        if (capWorksEmpty && m81Empty) return false;
                        
                        // If M81 has value but M25_CapWorks_2 is empty or zero, rule applies
                        if ((capWorksEmpty || capWorksIsZero) && !m81Empty) {
                            const m81Num = typeof m81Value === 'number' ? m81Value : parseFloat(m81Value as string);
                            if (!isNaN(m81Num) && m81Num !== 0) {
                                return true;
                            }
                        }
                        
                        return false;
                    },
                    run: (context) => {
                        context.raiseAlert({
                            // timestamp: Date.now(),
                            // id: nanoid(),
                            // ruleId: 'k75-capitalisation-review',
                            code: 'CAPWORKS_PRIOR_YEAR_ERROR',
                            // highlightRanges: ['L81'],
                            // focusRange: 'L81',
                            // outputCells: ['L81']
                        });
                    }
                }
            ]
        }
    }
}